import React, { PureComponent } from "react";
import styled from "styled-components";
import SweetAlert from "sweetalert-react";

import Auth from "../routes/Auth";
import RoundNotStartedGate from "../components/round-not-started";
import ProjectionsModal from "../components/ceo/decisions/projections-modal";
import SubmitDecisionsButton from "../components/ui-building-blocks/buttons/submit-decisions";
import { logError } from "../services/utils";
import EventEmitter from "../services/event-emitter";
import { getRequest } from "../api/ApiService";
import { submitDecisions } from "../api/SubmitDecisions";
import { fetchTeamDecisions, fetchTeamStatus } from "../api/TeamDecisionsService";
import RoleButtons from "../components/role-buttons";
import { view } from "@risingstack/react-easy-state";
import LastStepModal from "../components/get-started/last-step";

import OpsStyled from "components/operations/style";
import FinanceStyled from "components/finance/style";

function capitalizeFirstLetter(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const CEO_PROJECTIONS_URL = `${process.env.REACT_APP_API_URL}team/projections/`;

// ---------- styled components (lead-page only) ----------

const SignoffsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Mukta Vaani', Lato, sans-serif;

  th, td {
    padding: 14px 16px;
  }

  thead th {
    font-size: 12px;
    font-weight: 700;
    color: rgba(0, 27, 59, 0.6);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    text-align: left;
    border-bottom: 1px solid #E5E9F0;
  }

  tbody tr + tr td,
  tbody tr + tr th {
    border-top: 1px solid #E5E9F0;
  }

  th.dept {
    font-size: 15px;
    font-weight: 700;
    color: rgba(0, 27, 59, 0.94);
    text-align: left;
    width: 35%;
  }

  td.status {
    text-align: center;
    width: 35%;
  }

  td.cash {
    text-align: right;
    font-size: 16px;
    font-weight: 400;
    color: rgba(0, 27, 59, 0.94);
    font-variant-numeric: tabular-nums;
    width: 30%;
  }
`;

const CashHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-transform: none;
  letter-spacing: 0.02em;
  color: rgba(0, 27, 59, 0.6);
  font-size: 12px;

  b {
    color: #C62828;
    font-weight: 700;
  }
`;

// Two equal-width columns for the Signoffs (left) and Financial
// Projection (right) section cards. Stacks on narrow widths so each
// section still gets full breathing room on small screens.
const SectionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
  align-items: stretch;

  > * { height: 100%; }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectionTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Mukta Vaani', Lato, sans-serif;

  th, td {
    padding: 14px 16px;
  }

  thead th {
    font-size: 12px;
    font-weight: 700;
    color: rgba(0, 27, 59, 0.6);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    text-align: left;
    border-bottom: 1px solid #E5E9F0;
  }

  tbody tr + tr td,
  tbody tr + tr th {
    border-top: 1px solid #E5E9F0;
  }

  th.metric {
    font-size: 15px;
    font-weight: 700;
    color: rgba(0, 27, 59, 0.94);
    text-align: left;
  }

  td.amount {
    text-align: right;
    font-size: 16px;
    font-weight: 400;
    color: rgba(0, 27, 59, 0.94);
    font-variant-numeric: tabular-nums;
  }
`;

// ---------- component ----------

class CeoDecisions extends PureComponent {
  constructor(props) {
    super(props);

    this._isMounted = false;

    const hasAccess = Auth.getRole() === "CEO";

    this.state = {
      hasAccess,
      roleAlert: false,
      currentRound: props.currentRound,
      roundInUrl: props.roundInUrl,
      rounds: props.rounds,
      projections: [],
      loaded: false,
      spending: { pd: 0, pm: 0, op: 0, fn: 0 },
      submission_data: {
        pd: { date: "N/A", unaccepted: 0 },
        pm: { date: "N/A", unaccepted: 0 },
        op: { date: "N/A", unaccepted: 0 },
        fn: { date: "N/A", unaccepted: 0 },
      },
      financial_decisions: null,
      unaccepted_decisions: null,
      submitDecisionsLoadingDevelopment: true,
      submitDecisionsLoadingMarketing: true,
      submitDecisionsLoadingOperations: true,
      submitDecisionsLoadingFinance: true,
      isPDPreChecked: false,
      isPMPreChecked: false,
      isOPPreChecked: false,
      isFNPreChecked: false,
      showProjectionsModal: false,
      showProjectionsTour: false,
    };

    this.eventemitter = EventEmitter.getInstance();
  }

  reloadRole = () => {
    this.setState({ hasAccess: Auth.getRole() === "CEO" });
  };

  componentDidMount() {
    this._isMounted = true;
    this.fetchData();
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.state.roundInUrl !== nextProps.roundInUrl ||
      this.state.currentRound !== nextProps.currentRound ||
      (this.state.rounds &&
        this.state.rounds.length === 0 &&
        nextProps.rounds &&
        nextProps.rounds.length !== 0)
    ) {
      this.setState(
        {
          roundInUrl: nextProps.roundInUrl,
          currentRound: nextProps.currentRound,
          rounds: nextProps.rounds,
        },
        () => {
          this.fetchData();
          this.reloadRole();
        }
      );
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  getProjectionsUrl = () => {
    const { roundInUrl, currentRound } = this.state;
    let apiURL = CEO_PROJECTIONS_URL;
    const temporaryUser = Auth.getTemporaryUser();
    if (temporaryUser) {
      apiURL = `${apiURL}?team_id=${temporaryUser.team_id}`;
    }
    const literal = temporaryUser ? "&" : "?";
    const roundNumber = roundInUrl !== null && roundInUrl !== currentRound
      ? roundInUrl
      : currentRound;
    apiURL = `${apiURL}${literal}round_number=${roundNumber}`;
    return apiURL;
  };

  get roundNumber() {
    const { roundInUrl, currentRound } = this.state;
    if (roundInUrl !== null && roundInUrl !== currentRound) return roundInUrl;
    return currentRound;
  }

  fetchData = (showWarnings) => {
    const { rounds } = this.state;
    const roundNumber = this.roundNumber;

    if (rounds.length === 0) return;
    const round = rounds.find(i => i.number === roundNumber);
    if (!round) return;

    const requests = [
      fetchTeamDecisions(roundNumber, 'ceo'),
      fetchTeamStatus(roundNumber),
    ];
    if (!showWarnings) {
      requests.push(getRequest(this.getProjectionsUrl()));
    }

    Promise.all(requests)
      .then(([decisionsData, statusData, projectionsRes]) => {
        if (!this._isMounted) return;

        const { decisions, financial_decisions } = decisionsData;
        const { submission_data, unaccepted_decisions } = statusData;
        const projections = projectionsRes ? projectionsRes.body : this.state.projections;

        const spending = { pd: 0, pm: 0, op: 0, fn: 0 };
        spending.pd = decisions.product_development_role;
        spending.pm = decisions.product_marketing_role;
        // Operations cash impact is now a single backend-computed
        // aggregate (financial_decisions.team.operations) — no more
        // client-side composition from machines / production / DC /
        // facility components.
        spending.op = Number(
          (financial_decisions && financial_decisions.team && financial_decisions.team.operations) || 0
        );
        spending.fn = decisions.finance_role;

        // Pre-check flags drive the SubmitDecisionsButton's "Posted"
        // vs "POST DECISION" rendering. PM needs all three of its
        // decisions complete; the rest gate on a real journal entry.
        const pmHasPricing = (decisionsData.price_forecasts || []).some(i => i.player !== null);
        const pmHasPromos = (decisionsData.promos || []).some(i => i.player !== null);
        const pmHasSales = (decisionsData.sales || []).some(i => i.player !== null);
        const isPMPreChecked = pmHasPricing && pmHasPromos && pmHasSales;
        const isOPPreChecked = submission_data.op && submission_data.op.date !== 'N/A';
        const isFNPreChecked = submission_data.fn && submission_data.fn.date !== 'N/A';
        const isPDPreChecked = submission_data.pd && submission_data.pd.date !== 'N/A';

        // Signal to SubmitDecisions.js when every role has been posted
        // AND has no pending changes, so it can render the "you're all
        // set for this round" message instead of the plain success toast.
        // Both signals must hold: has-posted-once (submission_data.<key>.date)
        // AND nothing-newer-pending (unaccepted_decisions.<key> === 0).
        if (showWarnings) {
          const hasPosted = (k) => submission_data[k] && submission_data[k].date !== 'N/A';
          const noPending = (k) => (Number(unaccepted_decisions[k]) || 0) === 0;
          const allPosted =
            hasPosted('pd') && hasPosted('pm') && hasPosted('op') && hasPosted('fn') &&
            noPending('PD') && noPending('PM') && noPending('OP') && noPending('FI');
          showWarnings(decisionsData, { allPosted });
        }

        this.setState({
          spending,
          financial_decisions,
          projections,
          loaded: true,
          isPDPreChecked,
          isPMPreChecked,
          isOPPreChecked,
          isFNPreChecked,
          submission_data,
          unaccepted_decisions,
          submitDecisionsLoadingDevelopment: false,
          submitDecisionsLoadingMarketing: false,
          submitDecisionsLoadingOperations: false,
          submitDecisionsLoadingFinance: false,
        });
      })
      .catch(err => {
        logError(err, "CeoDecisions", "fetchData");
      });
  };

  showRoleAlert = () => this.setState({ roleAlert: true });
  hideRoleAlert = () => this.setState({ roleAlert: false });

  get showActionButtons() {
    if (this.state.hasAccess === false) return false;
    if (this.props.roundShouldBeFinished === true) return false;
    const { roundInUrl, currentRound, rounds } = this.state;
    if (roundInUrl !== null && roundInUrl !== currentRound) return false;
    const round = rounds.find(i => i.number === currentRound);
    if (!round) return false;
    return round.is_started === true && round.is_finished === false;
  }

  get isViewedRoundEnded() {
    const { roundInUrl, currentRound, rounds } = this.state;
    const viewedNumber = roundInUrl !== null ? roundInUrl : currentRound;
    const round = rounds.find(i => i.number === viewedNumber);
    if (!round) return false;
    return round.is_started === true && round.is_finished === true;
  }

  getSubmissionData = (type, showWarnings) => {
    this.setState({ [`submitDecisionsLoading${type}`]: true });
    this.fetchData(showWarnings);
  };

  submitDecisions = type => {
    const t = capitalizeFirstLetter(type);
    this.setState({ [`submitDecisionsLoading${t}`]: true });
    submitDecisions(
      type,
      this.props.roundInUrl || this.props.currentRound,
      (showWarnings) => this.getSubmissionData(t, showWarnings)
    );
  };

  openProjectionsModal = () => {
    if (!this.state.hasAccess) {
      this.showRoleAlert();
      return;
    }
    this.setState({ showProjectionsModal: true });
    // First-time walkthrough: fire the guided tour only when no
    // projection row has ever been saved for this team across any
    // round. The round-scoped projections in state cover the current
    // round only, so we hit the endpoint without round_number to see
    // if anything exists team-wide.
    const temporaryUser = Auth.getTemporaryUser();
    const url = temporaryUser
      ? `${CEO_PROJECTIONS_URL}?team_id=${temporaryUser.team_id}`
      : CEO_PROJECTIONS_URL;
    getRequest(url)
      .then(res => {
        if (!this._isMounted) return;
        const all = (res && res.body) || [];
        if (all.length === 0) {
          this.setState({ showProjectionsTour: true });
        }
      })
      .catch(err => logError(err, "CeoDecisions", "openProjectionsModal"));
  };

  closeProjectionsModal = () => this.setState({
    showProjectionsModal: false,
    showProjectionsTour: false,
  });

  endProjectionsTour = () => this.setState({ showProjectionsTour: false });

  startProjectionsTour = () => this.setState({ showProjectionsTour: true });

  // ---------- projection helpers ----------

  getProjection = (year) => {
    const found = (this.state.projections || []).find(
      i => i.target_round_number === year
    );
    return found || {};
  };

  getProjectionObjectModified = (year, offset) => {
    const obj = { ...this.getProjection(year) };
    delete obj.id;
    if (typeof obj.target_round_number === 'number') {
      obj.target_round_number += offset;
    }
    return obj;
  };

  get year1() {
    const { roundInUrl, currentRound } = this.state;
    if (roundInUrl !== null && roundInUrl !== currentRound) return roundInUrl;
    return currentRound;
  }

  get year2() { return this.year1 + 1; }
  get year3() { return this.year1 + 2; }

  get year1Projection() {
    return this.getProjection(this.year1);
  }

  // ProjectionsModal expects three years' projections; fall back to
  // copying year 1's row when 2/3 aren't on the backend yet so the
  // form has something to seed.
  get year2Projection() {
    const found = this.getProjection(this.year2);
    return Object.keys(found).length === 0
      ? this.getProjectionObjectModified(this.year1, 1)
      : found;
  }

  get year3Projection() {
    const found = this.getProjection(this.year3);
    return Object.keys(found).length === 0
      ? this.getProjectionObjectModified(this.year1, 2)
      : found;
  }

  projectionMetrics = (year) => {
    const proj = this.getProjection(year);
    const units = Number(proj.units_sold) || 0;
    const price = Number(proj.average_selling_price) || 0;
    const unitCost = Number(proj.average_unit_costs) || 0;
    const opex = Number(proj.other_operating_costs) || 0;
    const nonOp = Number(proj.non_operating_expenses) || 0;

    const revenues = units * price;
    const cogs = units * unitCost;
    const grossProfits = revenues - cogs;
    const operatingProfits = grossProfits - opex;
    const incomeBeforeTaxes = operatingProfits - nonOp;
    const taxes = incomeBeforeTaxes > 0 ? incomeBeforeTaxes * 0.3 : 0;
    const netProfits = incomeBeforeTaxes - taxes;

    return { revenues, grossProfits, operatingProfits, netProfits };
  };

  // ---------- render helpers ----------

  // Format cash to the full dollar value, rounded (no millions
  // abbreviation). Negative values render red to match the existing
  // "used" treatment.
  fmtCash = (v) => {
    const num = Number(v) || 0;
    const rounded = Math.round(Math.abs(num));
    const formatted = `$${rounded.toLocaleString('en-US')}`;
    return num < 0 ? `-${formatted}` : formatted;
  };

  fmtCashCell = (v) => {
    const num = Number(v) || 0;
    return (
      <span style={{ color: num < 0 ? '#C62828' : 'inherit' }}>
        {this.fmtCash(num)}
      </span>
    );
  };

  renderSignoffsRow(department, type, spend, isLoading, isPreChecked, extraProps = {}) {
    const dataKey = type === 'development' ? 'pd'
      : type === 'marketing' ? 'pm'
      : type === 'operations' ? 'op'
      : 'fn';
    const submission = this.state.submission_data[dataKey];
    const temporaryUser = Auth.getTemporaryUser();

    return (
      <tr key={department}>
        <th className="dept">{department}</th>
        {!temporaryUser && (
          <td className="status">
            <SubmitDecisionsButton
              id={`ceo-submit-decisions-${dataKey}`}
              tooltipPlacement="right"
              data={submission}
              isLoading={isLoading}
              isActive={this.showActionButtons}
              onClick={() => this.submitDecisions(type)}
              isPreChecked={isPreChecked}
              {...extraProps}
            />
          </td>
        )}
        <td className="cash">{this.fmtCashCell(spend)}</td>
      </tr>
    );
  }

  renderFinancialProjectionCard() {
    const metrics = this.projectionMetrics(this.year1);
    const fmt = (v) => `$${Math.round(v).toLocaleString('en-US')}`;
    const rows = [
      { label: 'Revenues', value: metrics.revenues, negative: false },
      { label: 'Gross Margins', value: metrics.grossProfits, negative: metrics.grossProfits < 0 },
      { label: 'Operating Profits', value: metrics.operatingProfits, negative: metrics.operatingProfits < 0 },
      { label: 'Net Profits', value: metrics.netProfits, negative: metrics.netProfits < 0 },
    ];

    return (
      <FinanceStyled.SectionCard>
        <FinanceStyled.SectionHeader>
          <FinanceStyled.SectionTitle>Financial Projection</FinanceStyled.SectionTitle>
          <FinanceStyled.SectionSubtitle>
            Year {this.year1} projection from the financial assumptions you've
            saved. Click <strong>Make Financial Projections</strong> to update.
          </FinanceStyled.SectionSubtitle>
        </FinanceStyled.SectionHeader>
        <ProjectionTable>
          <thead>
            <tr>
              <th>Line Items</th>
              <th style={{ textAlign: 'right' }}>Year {this.year1}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.label}>
                <th className="metric">{row.label}</th>
                <td className="amount">
                  <span style={{ color: row.negative ? '#C62828' : 'inherit' }}>
                    {row.negative ? `-${fmt(Math.abs(row.value))}` : fmt(row.value)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </ProjectionTable>
      </FinanceStyled.SectionCard>
    );
  }

  renderSignoffsCard() {
    const temporaryUser = Auth.getTemporaryUser();
    const {
      spending,
      submitDecisionsLoadingDevelopment,
      submitDecisionsLoadingMarketing,
      submitDecisionsLoadingOperations,
      submitDecisionsLoadingFinance,
      financial_decisions,
      unaccepted_decisions,
      isPDPreChecked,
      isPMPreChecked,
      isOPPreChecked,
      isFNPreChecked,
    } = this.state;

    return (
      <FinanceStyled.SectionCard>
        <FinanceStyled.SectionHeader>
          <FinanceStyled.SectionTitle>Signoffs</FinanceStyled.SectionTitle>
          <FinanceStyled.SectionSubtitle>
            Post each department's decisions any time before the round ends.
            You can repost whenever your plans change. The Cash column shows
            what each function has spent or received this round.
          </FinanceStyled.SectionSubtitle>
        </FinanceStyled.SectionHeader>
        <SignoffsTable>
          <thead>
            <tr>
              <th>Department</th>
              {!temporaryUser && <th style={{ textAlign: 'center' }}>Submission Status</th>}
              <th style={{ textAlign: 'right' }}>
                <CashHeader>
                  Cash <b>used</b>/received
                </CashHeader>
              </th>
            </tr>
          </thead>
          <tbody>
            {this.renderSignoffsRow(
              'Product Management', 'development',
              spending.pd, submitDecisionsLoadingDevelopment, isPDPreChecked
            )}
            {this.renderSignoffsRow(
              'Product Marketing', 'marketing',
              spending.pm, submitDecisionsLoadingMarketing, isPMPreChecked
            )}
            {this.renderSignoffsRow(
              'Operations', 'operations',
              spending.op, submitDecisionsLoadingOperations, isOPPreChecked
            )}
            {this.renderSignoffsRow(
              'Finance', 'finance',
              spending.fn, submitDecisionsLoadingFinance, isFNPreChecked,
              {
                financial_decisions,
                unaccepted_decisions,
                isRoundEnded: this.isViewedRoundEnded,
              }
            )}
          </tbody>
        </SignoffsTable>
      </FinanceStyled.SectionCard>
    );
  }

  render() {
    // Lock the page for students only when no round has ever been
    // started (`is_started` false on every record). Covers "no rounds
    // set up" (empty array) and "rounds scheduled but not yet running".
    // Deliberately does NOT fire once the game has finished — the
    // "hasn't started yet" copy would be misleading, and the user
    // should be free to browse historical decisions read-only.
    // `game.round` used to be part of this gate, but it's a
    // manually-set pointer that lags behind auto-started rounds —
    // relying on it locked students out of the running practice
    // round. Instructors don't see this because these decision
    // routes are student-only.
    const { rounds, rounds_loaded } = this.props;
    const hasEverStarted = (rounds || []).some(
      r => r && r.is_started === true
    );
    if (rounds_loaded && !hasEverStarted) {
      return <RoundNotStartedGate />;
    }

    const showActionButtons = this.showActionButtons;
    const {
      tourMode,
      location,
      router,
      currentRound: round,
    } = this.props;
    const {
      hasAccess,
      roleAlert,
      loaded,
      showProjectionsModal,
      showProjectionsTour,
    } = this.state;

    return (
      <OpsStyled.BodyWrapper>
        <LastStepModal
          location={location}
          router={router}
          url={`/decisions/ceo?round=${round}`}
        />
        <SweetAlert
          show={roleAlert}
          title="Information"
          text="Unfortunately, you are not authorized to make this decision"
          type="info"
          onConfirm={this.hideRoleAlert}
        />

        <OpsStyled.TitleWrapper>
          <OpsStyled.Title id="ceo-decisions-title">
            CEO Decisions
            {hasAccess && <RoleButtons type="ceo" />}
          </OpsStyled.Title>
        </OpsStyled.TitleWrapper>
        <OpsStyled.Subtitle>
          Set financial projections that guide Finance's capital raise, then sign off on team decisions by posting them once all roles have submitted.
        </OpsStyled.Subtitle>

        {showActionButtons && loaded && (
          <FinanceStyled.ManageButtonBar style={{ marginTop: 20 }}>
            <FinanceStyled.ManageButton onClick={this.openProjectionsModal}>
              Make Financial Projections
            </FinanceStyled.ManageButton>
          </FinanceStyled.ManageButtonBar>
        )}

        {loaded === false && (
          <div className="loader_spinner" style={{ marginTop: 24 }}>Loading...</div>
        )}

        {loaded === true && (
          <SectionRow>
            {this.renderSignoffsCard()}
            {this.renderFinancialProjectionCard()}
          </SectionRow>
        )}

        {showProjectionsModal && (
          // Full-page overlay (z-index 1500) so the projections form
          // covers the lead page like the product-decision popups. The
          // existing ProjectionsModal renders its content inline when
          // showModalState is true; wrapping it here gives that content
          // the same fixed-positioned canvas as the other decision
          // overlays without rewriting the form itself.
          <FinanceStyled.LenderOverlay>
            <div style={{
              maxWidth: 1160,
              margin: '0 auto',
              padding: '32px 40px 48px',
              fontFamily: "'Mukta Vaani', Lato, sans-serif",
            }}>
              <ProjectionsModal
                year_number1={this.year1}
                year1={this.year1Projection}
                year2={this.year2Projection}
                year3={this.year3Projection}
                currentRound={round}
                onSave={this.fetchData}
                hasAccess={hasAccess}
                showRoleAlert={this.showRoleAlert}
                tourMode={tourMode}
                showModalState={showProjectionsModal}
                showModal={this.openProjectionsModal}
                hideModal={this.closeProjectionsModal}
                showTour={showProjectionsTour}
                onTourEnd={this.endProjectionsTour}
                onStartTour={this.startProjectionsTour}
              />
            </div>
          </FinanceStyled.LenderOverlay>
        )}
      </OpsStyled.BodyWrapper>
    );
  }
}

export default view(CeoDecisions);



// WEBPACK FOOTER //
// ./src/containers/ceo-decisions.js